import '@/css/animate.css';
import '@/css/fonts.css';
import '@/css/globals.css';

import App, { AppProps } from 'next/app';
import { useRouter } from 'next/compat/router';
import { createContext, useEffect, useState } from 'react';

import { fetchAPI } from '@/lib/fetchAPI';
import { Language } from '@/lib/translate';

export const GlobalContext = createContext({} as any);

const MyApp = ({ Component, pageProps }: AppProps) => {
	const router = useRouter();
	const [device, setDevice] = useState('');
	const [isConsentGiven, setIsConsentGiven] = useState(false);
	const [formId, setFormId] = useState<string>('');
	const { footer, navigation } = pageProps as any;
	const lang = (router?.query?.lang as Language) ?? 'en';

	const detectForm = () => {
		const hash = window.location.hash.replace('#', '');

		if (hash.startsWith('form_')) {
			const id = hash.replace('form_', '');

			setFormId(id);
		}
	};

	const detectDevice = () =>
		setDevice(window.innerWidth <= 1024 ? 'mobile' : 'desktop');

	const testConsentBoxes = (checkboxes) => {
		const areChecked = Array.from(checkboxes)?.every(
			(item: any) => item.checked,
		);

		setIsConsentGiven(areChecked);
	};

	const handleLoad = () => {
		const checkboxes = document.querySelectorAll(
			'#onetrust-consent-sdk .category-switch-handler',
		);

		if (!checkboxes?.length) return;

		testConsentBoxes(checkboxes);

		const observer = new MutationObserver(() => {
			testConsentBoxes(checkboxes);
		});

		observer.observe(checkboxes[0], {
			attributes: true,
			attributeFilter: ['checked'],
		});
	};

	useEffect(() => {
		detectForm();
		detectDevice();

		window.addEventListener('load', handleLoad);
		window.addEventListener('resize', detectDevice);

		return () => {
			window.removeEventListener('load', handleLoad);
			window.removeEventListener('resize', detectDevice);
		};
	}, []);

	useEffect(() => {
		document.documentElement.lang = lang === 'ua' ? 'uk' : (lang ?? 'en');
	}, [lang]);

	return (
		<GlobalContext.Provider
			value={{
				lang,
				device,
				formId,
				footer,
				navigation,
				isConsentGiven,
			}}
		>
			<Component {...pageProps} />
		</GlobalContext.Provider>
	);
};

MyApp.getInitialProps = async (ctx) => {
	const appProps = await App.getInitialProps(ctx);
	const [footer, navigation] = await Promise.all([
		fetchAPI('/footer', {
			populate: [
				'footerItem',
				'logoViber',
				'logoQream',
				'localizations',
				'localizations.footerItem',
				'localizations.logoViber',
				'localizations.logoQream',
			],
		}),
		fetchAPI('/navigation', {
			populate: [
				'logo',
				'list',
				'list.dropdownItem',
				'socialMedia',
				'socialMedia.items',
				'socialMedia.items.logo',
				'socialMedia.items.logo.file',
				'contactUsButton',
				'localizations',
				'localizations.logo',
				'localizations.logo.file',
				'localizations.list',
				'localizations.list.dropdownItem',
				'localizations.socialMedia',
				'localizations.socialMedia.items',
				'localizations.socialMedia.items.logo',
				'localizations.socialMedia.items.logo.file',
				'localizations.contactUsButton',
			],
		}),
	]);

	return {
		...appProps,
		pageProps: {
			footer: footer?.data,
			navigation: navigation?.data,
		},
	};
};

export default MyApp;
